/**
 * AI:
 * Status: "confirmed"
 * Type: Component
 * SubType: WidgetConfig
 * Reason: Statistik-Komponente für die Workflow-Übersichtsseite. Stellt Verlaufsdiagramme, Gruppen-, Ergebnis- und Recently-Added-Widgets sowie Aggregats-Kennzahlen zu Workflow-Prozessen bereit.
 */
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {MenuItem} from "primeng/api";
import {WfProcessService} from "../../../../service/api/wf-process.service";
import {WfProcessStatusInternalEnum} from "../../../../enum/wf-process-status-internal.enum";
import {
    OverviewStatisticsBaseComponent
} from "../../../../../../core/shared/overview/overview-statistics-base.component";
import {WidgetFactory} from "../../../../../../core/shared/widget/service/widget.factory";
import {WidgetData} from "../../../../../../core/shared/widget/widget-data";
import {OverviewDto} from "../../../../../../core/shared/overview/dto/overview.dto";
import {MvsCoreService} from "../../../../../../core/shared/service/mvs-core.service";
import {ObjectRequestListGroupBy} from "../../../../../../core/shared/dto/object/request/object-request-list-group-by";
import {
    ObjectRequestListAttribute
} from "../../../../../../core/shared/dto/object/request/object-request-list-attribute";
import {
    DtoListAttributeRequestAggregateEnum
} from "../../../../../../core/shared/dto/enum/dto-list-attribute-request-aggregate.enum";
import {
    ObjectRequestEntityProcessingTypeEnum
} from "../../../../../../core/shared/dto/object/request/object-request-entity-processing-type.enum";
import {WidgetDataParam} from "../../../../../../core/shared/widget/widget-data-param";
import {Sorting} from "../../../../../../core/shared/misc/sorting";

@Component({
    selector: 'wf-statistics',
    templateUrl: 'wf-statistics.component.html',
    standalone: false
})
export class WfStatisticsComponent extends OverviewStatisticsBaseComponent implements OnInit, OnChanges, OnDestroy {

    inProgressProcess: number = 0;
    waitingProcess: number = 0;
    cancelledProcess: number = 0;
    completedProcess: number = 0;
    errorsProcess: number = 0;


    workflowProcessWidget: WidgetData;
    workflowTypeWidget: WidgetData;
    workflowResultWidget: WidgetData;
    recentlyAddedWorkflowsWidget: WidgetData;

    items: MenuItem[] | undefined;

    home: MenuItem | undefined;

    overviewDto: OverviewDto[];

    constructor(
        protected processService: WfProcessService,
        private router: Router,
        protected route: ActivatedRoute,
        protected coreService: MvsCoreService) {
        super(route, coreService)
    }

    ngOnInit(): void {


        this.items = [
            {label: 'Workflow-Modul'},
        ];

        this.home = {icon: 'pi pi-home', routerLink: '/'};
        super.ngOnInit();
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent() {
        this.refreshComponent();
    }

    /**
     * Refresh Component.
     */
    refreshComponent() {

        this.getCompletedProcess();
        this.refreshWorkflowProcessWidget();
        this.refreshProcessTypeWidgets();
        this.refreshProcessResultWidgets();
        this.refreshAddedWorkflows();
        this.initialized = true;
    }

    navigateToConfig() {
        this.router.navigate(['/wf/config']);
    }

    getCompletedProcess() {

        const objectRequestList = ObjectRequestListGroupBy.create(
            false,
            this.filterCriteria,
            [],
            ['processStatusInternal'],
            [new ObjectRequestListAttribute('processStatusInternal', "Anzahl", DtoListAttributeRequestAggregateEnum.count)],
            ObjectRequestEntityProcessingTypeEnum.regular
        );

        this.processService.groupBy(objectRequestList).subscribe(res => {

            this.overviewDto = [
                { label: 'Abgeschlossene Prozesse', value: 0 },
                { label: 'In Bearbeitung', value: 0 },
                { label: 'Wartende Prozesse', value: 0 },
                { label: 'Abgebrochene Prozesse', value: 0 },
                { label: 'Fehlgeschlagene Prozesse', value: 0 }
            ];

            for (let entry of res.entries) {
                if (entry['processStatusInternal'] === WfProcessStatusInternalEnum.completed) {
                    this.overviewDto.find(item => item.label === 'Abgeschlossene Prozesse').value = entry['processStatusInternal_count'];
                } else if (entry['processStatusInternal'] === WfProcessStatusInternalEnum.in_progress) {
                    this.overviewDto.find(item => item.label === 'In Bearbeitung').value = entry['processStatusInternal_count'];
                } else if (entry['processStatusInternal'] === WfProcessStatusInternalEnum.waiting) {
                    this.overviewDto.find(item => item.label === 'Wartende Prozesse').value = entry['processStatusInternal_count'];
                } else if (entry['processStatusInternal'] === WfProcessStatusInternalEnum.cancelled) {
                    this.overviewDto.find(item => item.label === 'Abgebrochene Prozesse').value = entry['processStatusInternal_count'];
                } else if (entry['processStatusInternal'] === WfProcessStatusInternalEnum.errors) {
                    this.overviewDto.find(item => item.label === 'Fehlgeschlagene Prozesse').value = entry['processStatusInternal_count'];
                }
            }


        });
    }

    refreshWorkflowProcessWidget() {
        /**
         * AI: Historien-Diagramm der Workflow-Prozesse pro Monat, gruppiert nach Prozesstyp.
         *     Quelle: wf.WfProcess (groupBy auf type + createdDate). Dient der Visualisierung
         *     des Mengengerüsts auf der Workflow-Übersichtsseite.
         */
        this.workflowProcessWidget = WidgetFactory.createWidget(
            'wf.workflow.overview.workflow.process.widget',
            'Workflow-Prozesse pro Monat',
            'history.chart',
            'list',
            'entity.groupBy',
            'wf.WfProcess',
            WidgetDataParam.create("fieldCategory", "type"),
            WidgetDataParam.create("fieldCategoryCount", "type_count"),
            WidgetDataParam.create("historyPeriodMode", "year"),
            WidgetDataParam.create("fieldDate", "createdDate"),
            WidgetDataParam.create("selectionMode", "single"),
            WidgetDataParam.create("size", "S")
        );
        this.workflowProcessWidget.dataProviderListRequest = ObjectRequestListGroupBy.create(
            true,
            this.filterCriteria,
            [],
            ["type", "createdDate"],
            [new ObjectRequestListAttribute("type", "Anzahl", DtoListAttributeRequestAggregateEnum.count)]
        );
    }

    refreshProcessTypeWidgets() {
        /**
         * AI: Kategorien-Widget für die Anzahl der Prozesstypen pro Workflow-Gruppe.
         *     Quelle: wf.WfProcessType (groupBy processGroup). Übersichts-Kachel für die
         *     Verteilung der konfigurierten Workflow-Typen.
         */
        this.workflowTypeWidget = WidgetFactory.createWidgetGroupBy(
            'wf.workflow.overview.workflow.type.widget',
            'Workflow-Gruppen',
            'category',
            'entity.groupBy',
            'wf.WfProcessType',
            'processGroup',
            'processGroup_count',
            this.filterCriteria,
            'processGroup',
            'Anzahl');
    }

    refreshProcessResultWidgets() {
        /**
         * AI: Kategorien-Widget mit der Verteilung der Workflow-Prozessergebnisse.
         *     Quelle: wf.WfProcess (groupBy processTypeResult). Zeigt das prozentuale Verhältnis
         *     der Ergebnistypen für die Erfolgs-/Fehlschlag-Analyse.
         */
        this.workflowResultWidget = WidgetFactory.createWidgetGroupBy(
            'wf.workflow.overview.workflow.process.result.widget',
            'Workflow-Prozessergebnisse',
            'category',
            'entity.groupBy',
            'wf.WfProcess',
            'processTypeResult',
            'processTypeResult_count',
            this.filterCriteria,
            'processTypeResult',
            'Anzahl');
    }

    refreshAddedWorkflows() {

        /**
         * AI: Tabellen-Widget mit den zuletzt angelegten Workflow-Typen, sortiert nach createdDate (desc).
         *     Quelle: wf.WfProcessType. Zeigt Administratoren auf einen Blick neu konfigurierte Workflows.
         */
        this.recentlyAddedWorkflowsWidget = WidgetFactory.createWidgetTableEntityBasicQl('wf.workflow.overview.recently.added.workflows', 'Kürzlich hinzugefügte Workflows', 'wf.WfProcessType', this.filterCriteriaForQuery, [new Sorting('e.createdDate', false)])

    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        if (changes["id"]) {
            this.refreshComponent();
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {

    }
}
